import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useFrame } from "@react-three/fiber";
import type { ThreeEvent } from "@react-three/fiber";
import type { Group, Mesh } from "three";
import type { BuildingFamily } from "@stahlfront/core";
import { claimAllProduction, fetchProductionList, getMe } from "../api.js";
import { BUILDING_LAYOUT, BUILDING_FOOTPRINT } from "./baseLayout.js";
import { BuildingMesh } from "./buildingMeshes.js";
import { useStore } from "../store.js";

/**
 * Phase 1.C base scene + Phase 2.A reward markers + Phase 2.C scaffold sprites.
 *
 * - Reads `buildings` from the store; ignores rows whose family has no layout slot.
 * - Each building is a single root <group> at its layout position; the group
 *   carries onPointerDown/onPointerOver so a single raycast hit propagates up
 *   without per-primitive listeners.
 * - For every building with at least one production_job in state="complete",
 *   a `RewardMarker` floats ~1.6 units above the building. Tapping the marker
 *   claims all complete jobs for that building.
 * - For every building with an active construction_job, a `ScaffoldMarker`
 *   (blue cone + ring) floats ~1.9 units above; rotates slowly with subtle
 *   ease and fades out when the job finalises.
 */
export function BaseScene(): JSX.Element {
  const buildings = useStore((s) => s.buildings);
  const setSelectedBuildingId = useStore((s) => s.setSelectedBuildingId);
  const productionJobs = useStore((s) => s.productionJobs);
  const setProductionJobs = useStore((s) => s.setProductionJobs);
  const constructionJobs = useStore((s) => s.constructionJobs);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [claimingBuildingId, setClaimingBuildingId] = useState<string | null>(null);

  /** Index: baseBuildingId -> count of jobs in state="complete". */
  const completeByBuilding = useMemo(() => {
    const m = new Map<string, number>();
    for (const j of productionJobs) {
      if (j.state === "complete") {
        m.set(j.baseBuildingId, (m.get(j.baseBuildingId) ?? 0) + 1);
      }
    }
    return m;
  }, [productionJobs]);

  /** Index: baseBuildingId -> active construction job (one per building max). */
  const activeUpgradeByBuilding = useMemo(() => {
    const m = new Map<string, true>();
    for (const j of constructionJobs) {
      if (j.state === "active") m.set(j.baseBuildingId, true);
    }
    return m;
  }, [constructionJobs]);

  const onClaim = async (buildingId: string): Promise<void> => {
    if (!me?.base.id || claimingBuildingId) return;
    setClaimingBuildingId(buildingId);
    try {
      const res = await claimAllProduction({ baseId: me.base.id, baseBuildingId: buildingId });
      setMe({ ...me, wallet: res.wallet });
      const list = await fetchProductionList(me.base.id);
      setProductionJobs(list.jobs);
    } catch (err) {
      console.warn("[stahlfront] in-scene claim failed:", err);
      const fresh = await getMe().catch(() => null);
      if (fresh) setMe(fresh);
    } finally {
      setClaimingBuildingId(null);
    }
  };

  return (
    <group>
      <mesh receiveShadow position={[0, -0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[14, 14, 1, 1]} />
        <meshStandardMaterial color="oklch(22% 0.02 55)" roughness={0.95} metalness={0.05} />
      </mesh>

      {buildings.map((b) => {
        const layout = BUILDING_LAYOUT[b.family as BuildingFamily];
        if (!layout) return null;
        const [x, z, rotY] = layout;
        const hovered = hoveredId === b.id;
        const onDown = (event: ThreeEvent<PointerEvent>): void => {
          event.stopPropagation();
          setSelectedBuildingId(b.id);
        };
        const onOver = (event: ThreeEvent<PointerEvent>): void => {
          event.stopPropagation();
          setHoveredId(b.id);
          document.body.style.cursor = "pointer";
        };
        const onOut = (event: ThreeEvent<PointerEvent>): void => {
          event.stopPropagation();
          setHoveredId((prev) => (prev === b.id ? null : prev));
          document.body.style.cursor = "";
        };

        const completeCount = completeByBuilding.get(b.id) ?? 0;
        const isUpgrading = activeUpgradeByBuilding.has(b.id);

        return (
          <group key={b.id} position={[x, 0, z]} rotation={[0, rotY, 0]}>
            <group
              position={[0, hovered ? 0.06 : 0, 0]}
              onPointerDown={onDown}
              onPointerOver={onOver}
              onPointerOut={onOut}
              userData={{ buildingId: b.id, family: b.family, footprint: BUILDING_FOOTPRINT }}
            >
              <BuildingMesh family={b.family as BuildingFamily} hovered={hovered} />
            </group>
            {completeCount > 0 ? (
              <RewardMarker
                count={completeCount}
                busy={claimingBuildingId === b.id}
                onClaim={() => void onClaim(b.id)}
              />
            ) : null}
            {isUpgrading ? <ScaffoldMarker /> : null}
          </group>
        );
      })}
    </group>
  );
}

interface RewardMarkerProps {
  readonly count: number;
  readonly busy: boolean;
  readonly onClaim: () => void;
}

function RewardMarker({ count, busy, onClaim }: RewardMarkerProps): JSX.Element {
  const groupRef = useRef<Group>(null);
  const innerRef = useRef<Mesh>(null);
  const t0 = useRef(performance.now() / 1000);

  useEffect(() => {
    t0.current = performance.now() / 1000;
  }, []);

  useFrame(() => {
    const t = performance.now() / 1000 - t0.current;
    const bob = 0.05 * Math.sin(t * 1.6 * Math.PI);
    const pulse = 1 + 0.07 * Math.sin(t * 1.6 * Math.PI);
    const g = groupRef.current;
    const inner = innerRef.current;
    if (g) g.position.y = 1.6 + bob;
    if (inner) {
      inner.scale.setScalar(pulse);
      inner.rotation.y += busy ? 0.06 : 0.018;
    }
  });

  const onDown = (event: ThreeEvent<PointerEvent>): void => {
    event.stopPropagation();
    if (busy) return;
    onClaim();
  };

  return (
    <group ref={groupRef} position={[0, 1.6, 0]}>
      <mesh
        ref={innerRef}
        onPointerDown={onDown}
        onPointerOver={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "";
        }}
      >
        <octahedronGeometry args={[0.22, 0]} />
        <meshStandardMaterial
          color={busy ? "oklch(58% 0.10 145)" : "oklch(70% 0.18 145)"}
          emissive="oklch(60% 0.18 145)"
          emissiveIntensity={busy ? 0.4 : 0.95}
          roughness={0.32}
          metalness={0.45}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, -0.18, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.32, 0.42, 24]} />
        <meshBasicMaterial color="oklch(72% 0.18 145)" transparent opacity={busy ? 0.25 : 0.55} side={2} />
      </mesh>
      {count > 1 ? (
        <mesh position={[0.32, 0.16, 0]}>
          <boxGeometry args={[0.18, 0.16, 0.02]} />
          <meshStandardMaterial color="oklch(96% 0.04 145)" emissive="oklch(86% 0.16 145)" emissiveIntensity={0.5} />
        </mesh>
      ) : null}
    </group>
  );
}

/**
 * Phase 2.C — under-construction scaffold sprite. Blue cone (the "drill")
 * + halo ring + small spinning gear. Rotates with cubic-eased phase shift
 * so the motion never feels mechanical.
 */
function ScaffoldMarker(): JSX.Element {
  const groupRef = useRef<Group>(null);
  const coneRef = useRef<Mesh>(null);
  const gearRef = useRef<Mesh>(null);
  const t0 = useRef(performance.now() / 1000);

  useFrame(() => {
    const t = performance.now() / 1000 - t0.current;
    const bob = 0.04 * Math.sin(t * 1.2 * Math.PI);
    const eased = Math.sin(t * 0.9 * Math.PI);
    const g = groupRef.current;
    const cone = coneRef.current;
    const gear = gearRef.current;
    if (g) g.position.y = 1.9 + bob;
    if (cone) cone.rotation.y += 0.012 + 0.004 * eased;
    if (gear) gear.rotation.y -= 0.06;
  });

  return (
    <group ref={groupRef} position={[0, 1.9, 0]}>
      <mesh ref={coneRef}>
        <coneGeometry args={[0.18, 0.4, 16]} />
        <meshStandardMaterial
          color="oklch(72% 0.16 245)"
          emissive="oklch(60% 0.18 245)"
          emissiveIntensity={0.7}
          roughness={0.38}
          metalness={0.45}
          toneMapped={false}
        />
      </mesh>
      <mesh ref={gearRef} position={[0, -0.14, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.18, 0.26, 18]} />
        <meshBasicMaterial color="oklch(80% 0.14 245)" transparent opacity={0.7} side={2} />
      </mesh>
      <mesh position={[0, -0.32, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.34, 0.46, 28]} />
        <meshBasicMaterial color="oklch(72% 0.16 245)" transparent opacity={0.4} side={2} />
      </mesh>
    </group>
  );
}
